import { useQuery } from '@tanstack/react-query';

import { fetchPlayers } from '@api-service/players';
import type { Player } from '@api-service/players';
import { useAccessToken } from '@hooks/useAccessToken';
import { useAuth0User } from '@hooks/useAuth0User';
import { useLeagueContext } from '@hooks/useLeagueContext';
import {
  buildPredictedRows,
  clampPredictedPlayerCount,
  hasFixedGameweeks,
  PREDICTED_STATS,
  resolvePredictedPosition,
  resolvePredictedStat,
  resolvePredictionWindow
} from '@lib/article-blocks/predicted-points';
import type {
  GameweekWindow,
  PredictedPlayerRow
} from '@lib/article-blocks/predicted-points';
import type { SanityPredictedPointsBlock } from '@lib/article-blocks/types';
import { hasSufficientPrivileges, ROLE_LEVELS } from '@lib/utils';

// Predictions are regenerated a few times a week as team news lands, so a
// reader moving between articles need not refetch the same top 10.
const PREDICTIONS_STALE_TIME_IN_MS = 5 * 60 * 1000;

export type ArticlePredictedPointsState =
  | { status: 'loading' }
  | {
      status: 'ready';
      rows: PredictedPlayerRow[];
      gameweekWindow: GameweekWindow;
    }
  | { status: 'unavailable' };

/**
 * The top players for one predicted points box, read by a member's browser
 * with their token; the API is the real gate, as it rejects prediction reads
 * without one. Anyone without Pro or Ultra, and any failure, is `unavailable`.
 */
export const useArticlePredictedPoints = (
  block: SanityPredictedPointsBlock
): ArticlePredictedPointsState => {
  const { user, isLoading: isUserLoading } = useAuth0User();
  const { accessToken, accessTokenIsFetching } = useAccessToken();
  const isEntitled = hasSufficientPrivileges(user, ROLE_LEVELS.PRO);

  const { context, isLoading: isContextLoading } = useLeagueContext({
    enabled: isEntitled && !hasFixedGameweeks(block.gameweekRange)
  });
  const gameweekWindow = resolvePredictionWindow(
    block.gameweekRange,
    context?.nextGameweek?.number
  );
  const stat = resolvePredictedStat(block.stat);
  const position = resolvePredictedPosition(block.position);
  const playerCount = clampPredictedPlayerCount(block.playerCount);

  const { data, isSuccess, isError } = useQuery<Player[]>({
    queryKey: [
      'article-predicted-points',
      stat,
      position,
      gameweekWindow?.minGameweek,
      gameweekWindow?.maxGameweek,
      playerCount,
      user?.sub
    ],
    queryFn: async () => {
      if (!accessToken || !gameweekWindow) return [];
      const response = await fetchPlayers({
        accessToken,
        sortBy: PREDICTED_STATS[stat].sortBy,
        sortDirection: 'desc',
        limit: playerCount,
        position,
        minGameweek: gameweekWindow.minGameweek,
        maxGameweek: gameweekWindow.maxGameweek
      });
      return response.data;
    },
    enabled: isEntitled && Boolean(accessToken) && Boolean(gameweekWindow),
    staleTime: PREDICTIONS_STALE_TIME_IN_MS,
    refetchOnWindowFocus: false,
    retry: false
  });

  if (isUserLoading) return { status: 'loading' };
  if (!isEntitled) return { status: 'unavailable' };
  if (!gameweekWindow) {
    return isContextLoading ? { status: 'loading' } : { status: 'unavailable' };
  }
  if (!accessToken) {
    return accessTokenIsFetching
      ? { status: 'loading' }
      : { status: 'unavailable' };
  }
  if (isError) return { status: 'unavailable' };
  if (isSuccess) {
    const rows = buildPredictedRows(data, stat, gameweekWindow, playerCount);
    return rows.length > 0
      ? { status: 'ready', rows, gameweekWindow }
      : { status: 'unavailable' };
  }
  return { status: 'loading' };
};
